// Shared FastAPI/Pydantic error formatter.
//
// Any FastAPI 422 validation error responds with
// `{ detail: [{ type, loc, msg, ... }, ...] }`. Passing that array
// straight into `toast.error()` (which Sonner treats as a ReactNode)
// crashes the whole page with React error #31 —
// "Objects are not valid as a React child (found: object with keys …)".
//
// `fmtErr` coerces any FastAPI error payload (string, list of
// validation errors, or a single error object) into a single
// toast-safe string so callers can safely do:
//
//     const err = await res.json().catch(() => ({}));
//     toast.error(fmtErr(err, 'Failed to save'));
//
// This is the canonical formatter — import from `lib/errors` instead
// of copy-pasting a local `fmtErr` in every modal component.
export const fmtErr = (payload, fallback = 'Something went wrong') => {
  if (!payload) return fallback;
  const d = payload.detail !== undefined ? payload.detail : payload;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const out = d.map((it) => {
      if (typeof it === 'string') return it;
      const field = Array.isArray(it?.loc) ? it.loc.slice(-1)[0] : '';
      const msg = it?.msg || 'Invalid';
      return field ? `${field}: ${msg}` : msg;
    }).join('; ');
    return out || fallback;
  }
  if (typeof d === 'object') return d.msg || d.message || JSON.stringify(d);
  return fallback;
};

// Convenience: parse a fetch Response, then format the error body.
// Usage:
//     if (!res.ok) { toast.error(await fmtResponseErr(res, 'Failed')); return; }
export const fmtResponseErr = async (res, fallback = 'Something went wrong') => {
  try {
    const body = await res.json();
    return fmtErr(body, fallback);
  } catch {
    return fallback;
  }
};
